import React, { useState } from "react";
import {
  Shield,
  Lock,
  Mail,
  AlertCircle,
  Loader2,
  CheckCircle,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../config/supabase";
import { useNavigate } from "react-router-dom";

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!email || !senha) {
      setError("Por favor, preencha todos os campos");
      setIsLoading(false);
      return;
    }

    const result = await login(email, senha);

    if (!result.success) {
      setError(result.error || "Erro durante o login");
    } else if (result.requiresPasswordReset) {
      // Redirecionar para página de reset de senha no primeiro login
      navigate("/reset-password-first-login");
    }

    setIsLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError("");
    setResetSuccess(false);

    if (!resetEmail) {
      setResetError("Por favor, informe seu email");
      return;
    }

    setResetLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        resetEmail,
        {
          redirectTo: `${window.location.origin}/reset-password`,
        },
      );

      if (resetError) {
        throw resetError;
      }

      setResetSuccess(true);
      setResetEmail("");

      // Ocultar modal após 3 segundos
      setTimeout(() => {
        setShowResetPassword(false);
        setResetSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("Erro ao enviar email de reset:", err);
      setResetError(
        err instanceof Error
          ? err.message
          : "Erro ao enviar email de redefinição de senha",
      );
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/logo/logoEhsFacil.png"
              alt="CIPA FÁCIL EHS Logo"
              className="w-16 h-16 object-contain"
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            CIPA FÁCIL EHS
          </h2>
          <p className="text-gray-600 text-sm">
            Sistema Completo de Gestão CIPA
          </p>
          <p className="text-gray-500 text-xs mt-1">
            100% Conforme NR-05 Vigente
          </p>
          {/* Nota: LoginForm não tem acesso ao CNAE, então mantém CIPA padrão */}
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-2xl p-8 space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Acesso ao Sistema
            </h3>
            <p className="text-gray-600 text-sm">
              Entre com suas credenciais para continuar
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 text-sm font-medium">
                  Erro no Login
                </p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="seu@email.com"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="senha"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="senha"
                  name="senha"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Entrar no Sistema
                </>
              )}
            </button>
          </form>

          <div className="text-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowResetPassword(true)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
            >
              Esqueci minha senha
            </button>
          </div>
        </div>

        {/* Modal Reset Password */}
        {showResetPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  Recuperar Senha
                </h3>
                <button
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetEmail("");
                    setResetError("");
                    setResetSuccess(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {resetSuccess ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-800 font-medium mb-2">
                    Email enviado com sucesso!
                  </p>
                  <p className="text-sm text-gray-600">
                    Verifique sua caixa de entrada e siga as instruções para
                    redefinir sua senha.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-gray-600 text-sm mb-4">
                    Digite seu email cadastrado. Enviaremos um link para
                    redefinir sua senha.
                  </p>

                  {resetError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-red-700 text-sm">{resetError}</p>
                    </div>
                  )}

                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label
                        htmlFor="resetEmail"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="resetEmail"
                          name="resetEmail"
                          type="email"
                          required
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="seu@email.com"
                          disabled={resetLoading}
                        />
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowResetPassword(false);
                          setResetEmail("");
                          setResetError("");
                          setResetSuccess(false);
                        }}
                        disabled={resetLoading}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={resetLoading || !resetEmail}
                        className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {resetLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          "Enviar Link"
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Desenvolvido com base na <strong>Portaria MTP nº 4.219/2022</strong>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Garantia total de conformidade legal
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Desenvolvido por <strong>Equilibrium Tecnologia</strong> -{" "}
            <a
              href="https://www.equilibriumtecnologia.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              www.equilibriumtecnologia.com.br
            </a>
          </p>
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} Equilibrium Tecnologia. Todos os
            direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
